/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListCimdClientPresetsRequest,
  ListCimdClientPresetsSecurity,
} from "../models/operations/listcimdclientpresets.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildCimdClientPresetsQuery,
  CimdClientPresetsQueryData,
  prefetchCimdClientPresets,
  queryKeyCimdClientPresets,
} from "./cimdClientPresets.core.js";
export {
  buildCimdClientPresetsQuery,
  type CimdClientPresetsQueryData,
  prefetchCimdClientPresets,
  queryKeyCimdClientPresets,
};

export type CimdClientPresetsQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listPresets userSessionIssuersCimdClients
 *
 * @remarks
 * List Speakeasy's curated CIMD preset catalog. Issuers whose admission mode is 'presets' admit every enabled entry here automatically, with no per-issuer configuration. Presets mode is opt-in; an issuer without an explicit mode uses 'open' and evaluates this catalog only for its shadow measurement. The catalog is global and contains no tenant data.
 */
export function useCimdClientPresets(
  request?: ListCimdClientPresetsRequest | undefined,
  security?: ListCimdClientPresetsSecurity | undefined,
  options?: QueryHookOptions<
    CimdClientPresetsQueryData,
    CimdClientPresetsQueryError
  >,
): UseQueryResult<CimdClientPresetsQueryData, CimdClientPresetsQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildCimdClientPresetsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listPresets userSessionIssuersCimdClients
 *
 * @remarks
 * List Speakeasy's curated CIMD preset catalog. Issuers whose admission mode is 'presets' admit every enabled entry here automatically, with no per-issuer configuration. Presets mode is opt-in; an issuer without an explicit mode uses 'open' and evaluates this catalog only for its shadow measurement. The catalog is global and contains no tenant data.
 */
export function useCimdClientPresetsSuspense(
  request?: ListCimdClientPresetsRequest | undefined,
  security?: ListCimdClientPresetsSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    CimdClientPresetsQueryData,
    CimdClientPresetsQueryError
  >,
): UseSuspenseQueryResult<
  CimdClientPresetsQueryData,
  CimdClientPresetsQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildCimdClientPresetsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setCimdClientPresetsData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: CimdClientPresetsQueryData,
): CimdClientPresetsQueryData | undefined {
  const key = queryKeyCimdClientPresets(...queryKeyBase);

  return client.setQueryData<CimdClientPresetsQueryData>(key, data);
}

export function invalidateCimdClientPresets(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "userSessionIssuersCimdClients",
      "listPresets",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllCimdClientPresets(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "userSessionIssuersCimdClients", "listPresets"],
  });
}
